import { useMemo } from 'react';
import type {
  TacticsBranch,
  TacticsKeyframe,
  TacticsVoiceover,
} from '@4cm/core/tactics';
import { getKeyframeMarkers } from '../../lib/tacticsKeyframes';
import { HudChip } from './HudChip';
import { HudHint } from './HudHint';
import { KeyframeScrubber } from './KeyframeScrubber';
import { BranchPicker } from './BranchPicker';

/**
 * Walkthrough mode HUD composite — Phase 2's keyframe playback paired
 * with Phase 3's per-keyframe voice-over transcripts.
 *
 * The board mounts this component once its layout has ≥2 keyframes;
 * for single-snapshot layouts we render nothing so the existing run-
 * playback HUD stays alone in the row. The boards still own arrows +
 * field rendering — Walkthrough only drives token positions (via the
 * hook's `positions` map) + the narrate-as-you-go TTS.
 *
 * Visual layout:
 *   [Walk] [Pause] [Reset]  ──── narrating: "Build-up …" / Mute
 *   ────────────── scrubber ──────────────
 *   [Main] [if intercepted] [if press wins it]   (branch chips, if any)
 */
export interface WalkthroughControlsProps {
  /** Main-sequence keyframes from the active layout. */
  keyframes: TacticsKeyframe[];
  /** Optional branches; surfaced as chips when present. */
  branches?: TacticsBranch[];
  /** Per-keyframe voiceovers — wired in so the indicator pill can
   *  surface the first 40 chars of the active narration. */
  voiceovers?: TacticsVoiceover[];
  /** Hook output. The composite is intentionally controlled — the
   *  board owns the hook so it can pipe positions into render
   *  overrides without prop-drilling through this component. */
  isPlaying: boolean;
  progress: number;
  activeKeyframeId: string | null;
  activeBranchId: string | undefined;
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
  onScrub: (next: number) => void;
  onSelectBranch: (branchId: string | undefined) => void;
  /** Master TTS toggle — drives the mute chip. */
  ttsEnabled: boolean;
  onToggleTts: () => void;
}

export function WalkthroughControls({
  keyframes,
  branches,
  voiceovers,
  isPlaying,
  progress,
  activeKeyframeId,
  activeBranchId,
  onPlay,
  onPause,
  onReset,
  onScrub,
  onSelectBranch,
  ttsEnabled,
  onToggleTts,
}: WalkthroughControlsProps) {
  // Markers reflect the MAIN sequence so the coach always sees the
  // same skeleton — branch chips switch the path but don't shuffle
  // the marker row underneath them.
  const markers = useMemo(() => getKeyframeMarkers(keyframes), [keyframes]);

  // Lookup the active voice-over transcript for the narrating pill.
  // We surface only the first 40 chars so the HUD doesn't reflow on
  // long transcripts.
  const activeTranscript = useMemo(() => {
    if (!activeKeyframeId) return null;
    const found = (voiceovers ?? []).find((v) => v.keyframeId === activeKeyframeId);
    return found?.transcript ?? null;
  }, [voiceovers, activeKeyframeId]);

  // Filter branches whose parent is the active keyframe — those are
  // the ones meaningfully selectable right now. If no active keyframe
  // (empty layout) we show all branches so a coach can still pre-pick
  // a path.
  const relevantBranches = useMemo(() => {
    if (!branches || branches.length === 0) return [];
    if (!activeKeyframeId) return branches;
    return branches.filter((b) => b.parentKeyframeId === activeKeyframeId);
  }, [branches, activeKeyframeId]);

  // Single-snapshot layouts: nothing to walk through, render nothing.
  // Same rule applies to layouts the migration hasn't touched yet —
  // the boards' existing snapshot UI handles those cases.
  if (!keyframes || keyframes.length < 2) return null;

  /** Click handler for a marker tick — jump to that keyframe's start. */
  const handleMarkerSelect = (m: { t: number; id?: string }) => {
    onScrub(m.t);
  };

  const transcriptPreview = activeTranscript
    ? activeTranscript.length > 40
      ? `${activeTranscript.slice(0, 40)}…`
      : activeTranscript
    : null;

  return (
    <div
      className="thud__walkthrough"
      data-testid="walkthrough-controls"
      role="group"
      aria-label="Walkthrough playback"
    >
      <div className="thud__walkthrough-row">
        <HudChip
          active={isPlaying}
          ariaLabel={isPlaying ? 'Pause walkthrough' : 'Play walkthrough'}
          onClick={isPlaying ? onPause : onPlay}
        >
          {isPlaying ? '❚❚ Pause' : '▶ Walk'}
        </HudChip>
        <HudChip ariaLabel="Reset walkthrough to start" onClick={onReset}>
          ◼ Reset
        </HudChip>
        {transcriptPreview && ttsEnabled && (
          <HudHint>narrating: {transcriptPreview}</HudHint>
        )}
        <HudChip
          active={!ttsEnabled}
          ariaLabel={ttsEnabled ? 'Mute walkthrough narration' : 'Unmute walkthrough narration'}
          onClick={onToggleTts}
        >
          {ttsEnabled ? '🔊 Voice' : '🔇 Muted'}
        </HudChip>
      </div>
      <KeyframeScrubber
        value={progress}
        onChange={onScrub}
        ariaLabel="Walkthrough progress"
        markers={markers}
        onMarkerSelect={handleMarkerSelect}
      />
      {relevantBranches.length > 0 && (
        <BranchPicker
          branches={relevantBranches}
          activeBranchId={activeBranchId}
          onSelect={onSelectBranch}
        />
      )}
    </div>
  );
}
